import { defineStore } from 'pinia';

/**
 * 将扁平的DICOM/云胶片元数据解析为分组树结构
 * @param {Object} { rawData: PAI1, PAJ1 , PAA06}
 * @returns {Array<Object>} 格式化后的分组数据
 */
function formatDicomSeriesGroups({ PAI1, PAJ1, PAA06, rType }) {
  if (/(dr|dx)/gi.test(rType)) {
    let list = [];
    /* {
      title: `${modality}`,
      protocolType,
      desc: `${protocolDesc} [${sliceCount}张]`,
      part: `推测: ${inferredPart} (基于Z轴坐标与扫描范围推断，非临床精准定位)`,
      time: startTime,
      explain: PAA06 + ' ' + children.length,
      len: children.length,
      children,
    } */

    for (let item of PAI1) {
      for (let children of PAJ1) {
        list.push({
          title: children.BBC01,
          time: item.PAI08,
          explain: PAA06 + ' ' + 1,
          len: 1,
          children: [children],
        });
      }
    }
    return list;
  }
  if (/mr/gi.test(rType)) {
    let list = [];

    for (let item of PAI1) {
      let children = PAJ1.filter((i) => i.PAI01 === item.PAI01);
      list.push({
        title: item.BBC01,
        time: item.PAI08,
        explain: PAA06 + ' ' + children.length,
        len: children.length,
        children: children,
      });
    }

    // throw new Error();
    return list;
  }

  let rawData = PAI1;
  if (!Array.isArray(rawData) || rawData.length === 0) return [];

  // 1. 按 Series UID (PAI01) 分组，缺失则降级使用 PAI01
  const seriesMap = new Map();
  rawData.forEach((item) => {
    const seriesId = item.PAI01;
    if (!seriesMap.has(seriesId)) {
      seriesMap.set(seriesId, { meta: item, slices: [] });
    }
    // 收集含切片信息的记录（通常含 PAJ30 或 PAJ01）
    if (item.PAJ30 || item.PAJ01) {
      seriesMap.get(seriesId).slices.push(item);
    }
  });

  // 2. 转换每个序列为指定结构
  let groups = Array.from(seriesMap.values()).map(({ meta, slices }) => {
    const modality = meta.BBC01 || 'CT';
    const protocol = (meta.PAI11 || '').trim();
    const sliceCount = meta.PAI06 || slices.length;
    const startTime = meta.PAI08 || '';

    let children = PAJ1.filter((item) => item.PAI01 === meta.PAI01);

    // const randomIndex = 10;
    // children = children.filter((item, index) => !(index % randomIndex));

    //  解析协议名称，生成 title 和 desc
    let protocolType = '未知序列';
    let protocolDesc = '';
    if (/^\d+(\.\d+)?\s*x\s*\d+(\.\d+)?$/i.test(protocol)) {
      const [thickness, interval] = protocol.split('x').map((s) => s.trim());
      protocolType = `标准重建 (${protocol.replace(/\s+/g, '×')})`;
      protocolDesc = `层厚${thickness}mm / 间隔${interval}mm`;
    } else if (/3d/i.test(protocol)) {
      protocolType = `三维重建 (${protocol})`;
      protocolDesc = '高精度三维重建数据';
    } else if (!protocol) {
      protocolType = '辅助序列';
      protocolDesc = '未标识协议参数';
    } else {
      protocolType = protocol;
      protocolDesc = protocol;
    }

    // 🌍 基于 Z 轴坐标推断部位（启发式规则，务必标注非精准）
    const zValues = slices
      .map((s) => s.PAJ41)
      .filter(Boolean)
      .map((pos) => {
        const parts = pos.split('\\');
        return parseFloat(parts[2]); // DICOM IPP 第3位为 Z 轴
      })
      .filter((n) => !isNaN(n));

    let inferredPart = '未知扫描区域';
    if (zValues.length > 1) {
      const minZ = Math.min(...zValues);
      const maxZ = Math.max(...zValues);
      const avgZ = (minZ + maxZ) / 2;
      const range = maxZ - minZ;

      // 常见 CT 扫描 Z 轴经验范围（因设备原点不同，仅作相对参考）
      if (range > 450) inferredPart = '胸腹联合/大范围扫描';
      else if (avgZ > -400 && avgZ < -100) inferredPart = '头颈部/上胸部';
      else if (avgZ <= -400 && avgZ > -700) inferredPart = '胸部';
      else if (avgZ <= -700 && avgZ > -1000) inferredPart = '腹部/盆腔';
      else inferredPart = '下肢/局部扫描';
    }

    // 📦 按 Z 轴排序切片（保证浏览时从上到下或从下到上连续）
    const sortedSlices = [...slices].sort((a, b) => {
      const zA = parseFloat(a.PAJ41?.split('\\')[2]);
      const zB = parseFloat(b.PAJ41?.split('\\')[2]);
      return (isNaN(zA) ? 0 : zA) - (isNaN(zB) ? 0 : zB);
    });
    // `[${children.length}] ` +
    // - ${protocolType}
    return {
      title: `${modality}`,
      protocolType,
      desc: `${protocolDesc} [${sliceCount}张]`,
      part: `推测: ${inferredPart} (基于Z轴坐标与扫描范围推断，非临床精准定位)`,
      time: startTime,
      explain: PAA06 + ' ' + children.length,
      len: children.length,
      children,
    };
  });

  groups = groups.filter((item) => {
    // 3d|
    return !/[辅助序列]/gi.test(item.protocolType);
  });

  // 3. 智能排序：标准重建优先 → 按时间正序 → 三维重建置底
  groups.sort((a, b) => {
    const isStdA = a.title.includes('标准重建');
    const isStdB = b.title.includes('标准重建');
    const is3DA = a.title.includes('三维重建');
    const is3DB = b.title.includes('三维重建');

    if (isStdA && !isStdB) return -1;
    if (!isStdA && isStdB) return 1;
    if (is3DA && !is3DB) return 1; // 3D 放最后
    if (!is3DA && is3DB) return -1;

    // 同类型按采集时间排序
    const tA = new Date(a.time).getTime() || 0;
    const tB = new Date(b.time).getTime() || 0;
    return tA - tB;
  });

  return groups;
}

export const useDicomRawStore = defineStore('dicomRaw', {
  state: () => ({
    rawData: {},
    dicomData: [],
    code: '',
    hid: '',
    userInfo: {
      gender: '',
      hospitalName: '',
      patientName: '',
    },
  }),
  actions: {
    setDicomData(data) {
      this.dicomData = data;
      this.formatDicomData(data);
    },
    setCode(code) {
      this.code = code;
    },
    setHid(id) {
      this.hid = id;
    },
    setUserInfo(data) {
      this.userInfo = data;
    },
    formatDicomData(data) {
      // this.dicomData = data;

      console.log(data, 'data');
      console.log(
        formatDicomSeriesGroups({
          PAI1: data.PAI1.rows,
          PAJ1: data.PAJ1.rows,
          PAA06: data.reportId,
          rType: data.rType,
        }),
        'formatDicomData===99999999999999',
        data.PAA06
      );

      this.setCode(data.rType);

      this.dicomData = formatDicomSeriesGroups({
        PAI1: data.PAI1.rows,
        PAJ1: data.PAJ1.rows,
        PAA06: data.reportId,
        rType: data.rType,
      });
    },
  },

  getters: {
    dicomUrl() {
      switch (this.hid) {
        case '1':
          return 'https://dicom-1329021783.cos.ap-chengdu.myqcloud.com/';
        case '160':
          return 'https://dicom-xw-1329021783.cos.ap-chengdu.myqcloud.com';
        case '146':
          return 'https://dicom-xw-1329021783.cos.ap-chengdu.myqcloud.com/';
        case '3':
          return 'https://dicom-bs-1329021783.cos.ap-chengdu.myqcloud.com/';
        default:
          return 'https://dicom-1329021783.cos.ap-chengdu.myqcloud.com/';
      }
    },
  },
});
